import { useOutletContext, useParams } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import DatePicker from 'react-datepicker'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'
import 'react-datepicker/dist/react-datepicker.css'

import Icon from '../../../components/Icon'
import TimestampWithUTCTooltip from '../../../components/TimestampWithUTCTooltip'

import { NotificationTypes, addNotification } from '../../../state/actions/notificationActions.js'
import { createTasks } from '../../../state/actions/taskActions.js'

import { TaskType, ComponentDeviceType, SortDirectionType } from '../../../utils/constants.js'
//import { computeSortedData } from '../../../utils/table.js'

function PushFormsModal({ showSignal, handleCancel, taskType, deviceFormsSignal, selectedDevices, devicePciVersionSignal, showDownloadProgressSignal, downloadProgressMessageSignal }) {

	const { beId } = useParams()
	const { user, config, accessToken, permissionMasks, permissions } = useOutletContext()

	const bulkLabelSignal = useSignal('')

	const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

	const defaultDateComputed = useComputed(() => {
		const currentDate = new Date()
		const nextDay = new Date(currentDate)
		nextDay.setDate(currentDate.getDate() + 1) // Move to the next day
		nextDay.setHours(0, 0, 0, 0) // Set time to midnight
		return nextDay;
	})

	const dateSignal = useSignal(defaultDateComputed.value)
	const deviceSignal = useSignal("")
	const modelNumberPatternSignal = useSignal('')
	const runImmediatelySignal = useSignal(false)
	const selectedFormSignal = useSignal(null)
	const sortColumnSignal = useSignal('')

	
	const enableApplyComputed = useComputed(() => {
		if (selectedFormSignal.value == null || (runImmediatelySignal.value == false && dateSignal.value == null)) {
			return false
		}
		else if (taskType == TaskType.UpdateDeviceFirmware && devicePciVersionSignal.value == 0) {
			return false
		}
		return true
	})


	const deviceChanged = function (event) {
		deviceSignal.value = event.target.value
		modelNumberPatternSignal.value = ''
		devicePciVersionSignal.value = getDevicePciVersion(deviceSignal.value)
	}

	//const sortedDataComputed = useComputed(computeSortedData(deviceFormsSignal, sortColumnSignal, sortDirectionSignal))

	const modelNumberPatternChanged = function (event) {
		modelNumberPatternSignal.value = event.target.value
		devicePciVersionSignal.value = getModelNumberPatternPciVersion(modelNumberPatternSignal.value)
	}

	const isSelfSeries = (deviceType) => {
		if (!(deviceType in ComponentDeviceType)) {
			return false
		}
		deviceType = ComponentDeviceType[deviceType]
		switch (deviceType) {
			case ComponentDeviceType.Self2000:
			case ComponentDeviceType.Self3000:
			case ComponentDeviceType.Self4000:
			case ComponentDeviceType.Self5000:
			case ComponentDeviceType.Self70008000:
				return true
			default:
				return false
		}
	}

	const pushFormToDevice = async function () {
		try {
			showSignal.value = false
			downloadProgressMessageSignal.value = "Pushing forms to device..."
			showDownloadProgressSignal.value = true
			let runAfter
			const now = (new Date()).toISOString()
			if (runImmediatelySignal.value) {
				runAfter = now
			}
			else {
				runAfter = (dateSignal.value).toISOString()
			}
			let tasks = []
			for (let i in selectedDevices) {
				let selectedDevice = selectedDevices[i]
				let deviceType
				if (selectedDevice.deviceStatusDeviceType) {
					if (isSelfSeries(selectedDevice.deviceStatusDeviceType)) {
						deviceType = selectedDevice.deviceStatusDeviceType
					}
					else {
						deviceType = selectedDevice.deviceStatusDeviceType.concat("RBA")
					}
				} else {
					deviceType = "nullRBA"
				}
				const keys = [
					'componentId',
					'deviceConfigApiTerminalId',
					'deviceStatusDeviceType',
					'businessEntityId',
					'machinePermanentId'
				]
				let missing = []
				for (let i = 0; i < keys.length; i++) {
					let key = keys[i]
					if (!(key in selectedDevice)) {
						missing.push(key)
					}
				}
				if (missing.length > 0) {
					throw new Error("Missing the following keys: ".concat(missing.join(", ")))
				}
				let task = {
					componentId: selectedDevice.componentId,
					instructions: JSON.stringify({
						deviceType: deviceType,
						apiTid: selectedDevice.deviceConfigApiTerminalId,
						formBusinessEntityId: selectedFormSignal.value.businessEntityId,
						formId: selectedFormSignal.value.id,
						pciVersion: (devicePciVersionSignal && devicePciVersionSignal.value == 0) ? null : devicePciVersionSignal.value,
						terminalName: selectedDevice.deviceStatusDeviceType,
						fileName: selectedFormSignal.value?.fileLocation?.key?.split('/')?.pop(),
						includesFirmware: !!selectedFormSignal.value?.fileLocation?.key?.endsWith('.zip')
					}),
					lastUpdatedAt: now,
					runAfter: runAfter,
					type: taskType,
					businessEntityId: selectedDevice.businessEntityId,
					machinePermanentId: selectedDevice.machinePermanentId,
					bulkLabel: bulkLabelSignal.value.length ? bulkLabelSignal.value : null
				}
				tasks.push(task)
			}
			createTasks(beId, tasks, config, accessToken).then(
				() => {
					addNotification(`Device form pushed successfully.`, NotificationTypes.SUCCESS, true)
					handleCancel()
				}
			).catch(
				(x) => {
					console.trace(x.toString())
					addNotification(`Unable to push form to device. ${x}`, NotificationTypes.ERROR, true)
				}
			)
		}
		catch (x) {
			console.trace(x.toString())
			addNotification(`Unable to push form to device. ${x}`, NotificationTypes.ERROR, true)
		}
		finally {
			showDownloadProgressSignal.value = false
		}
	}

	const handleHeaderTableClick = (datafield) => {
		// if already sorted on this column then toggle sort direction
		if (datafield == sortColumnSignal.value) {
			sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
			return
		}

		// update sort column - the computed functions will then run and render the sorted data
		sortColumnSignal.value = datafield
	}

	function TableColumnHeader({ title, sortable, datafield }) {
		// non-sortable columns do not have click handlers
		if (!sortable) {
			return (
				<th>{title}</th>
			)
		}

		// sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
		if (datafield != sortColumnSignal.value) {
			return (
				<th /*className="sortable-table-header"*/ onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
			)
		}

		// datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
		if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
			return (
				<th /*className="sortable-table-header"*/ onClick={() => handleHeaderTableClick(datafield)}>{title}
					<Icon icon="caret-down" width="16" height="16"></Icon>
				</th>
			)
		}

		// else must be ascending
		return (
			<th /*className="sortable-table-header"*/ onClick={() => handleHeaderTableClick(datafield)}>{title}
				<Icon icon="caret-up" width="16" height="16"></Icon>
			</th>
		)
	}

	function TableRow({ item }) {
		const id = "radioSelectForm".concat(item.id)
		return (
			<tr key={item.id}>
				<td style={{ height: '65.5px', width: '65.5px' }}>
					<label htmlFor={id}>
						<input type="radio" className="form-check-input" name="formId" aria-label="Select Device Form" checked={selectedFormSignal.value != null && selectedFormSignal.value.id == item.id} onChange={() => selectedFormSignal.value = item} id={id} />
					</label>
				</td>
				<td>{item.name}</td>
				<td>{item.deviceType}</td>
				<td><TimestampWithUTCTooltip>{item.lastUpdatedAt}</TimestampWithUTCTooltip></td>
			</tr>
		)
	}

	function TableRows({ items }) {
		if (items && items.length) {
			const rows = items.map(item => {
				return <TableRow item={item} key={`${item.id}`} />
			})
			return (
				<tbody>{rows}</tbody>
			)
		}
		else if (items) {
			return (
				<tbody><tr><td colSpan="18" className="text-center">This business entity does not contain any device forms</td></tr></tbody>
			)
		}
	}

	const getDevicePciVersion = (dev) => {
		try {
			switch (dev) {
				case "": //uninitialized
				case "ipp320":
				case "ipp350":
				case "isc250":
				case "iup250":
					return 0 //using PCI version 0 => check model number pattern for this device
				case "iwl220":
				case "iwl250":
				case "iwl252":
				case "iwl228":
				case "iwl258":
				case "icm122":
				case "imp322":
				case "imp352":
					return 3
				case "isc480":
				case "imp657":
				case "imp627":
					return 4
				default:
					throw Error("Invalid device key \""+dev+"\"")
			}
		}
		catch (x) {
			console.trace(x.toString())
			window.alert(x.toString())
		}
	}

	const getModelNumberPatternPciVersion = (model) => {
		try {
			switch (model) {
				case "":
					return 0 //uninitialized
				case "IPP320-11Txxxxx":
				case "IPP350-11Txxxxx":
				case "ISC250-01Txxxxx":
				case "IUP250-01Txxxxxx":
					return 3
				case "IPP320-31Txxxxx":
				case "IPP350-31Txxxxx":
				case "ISC250-31Txxxxx":
				case "IUP250LE (with 13_23 =0)":
				case "IUP250-11Txxxxx (with 13_23=1)":
					return 4
				default:
					throw Error("Invalid model number string \""+model+"\"")
			}
		}
		catch (x) {
			console.trace(x.toString())
			window.alert(x.toString())
		}
	}

	const getDeviceModelNumberPatterns = (device) => {
		switch (device) {
			case "ipp320":
				return ["IPP320-31Txxxxx", "IPP320-11Txxxxx"]
			case "ipp350":
				return ["IPP350-31Txxxxx", "IPP350-11Txxxxx"]
			case "isc250":
				return ["ISC250-31Txxxxx", "ISC250-01Txxxxx"]
			case "iup250":
				return ["IUP250LE (with 13_23 =0)", "IUP250-11Txxxxx (with 13_23=1)", "IUP250-01Txxxxxx"]
			default:
				return []
		}
	}

	function ModelNumberPatternSelect({device}) {
		if (device == null) {
			return null
		}
		let modelNumbers = getDeviceModelNumberPatterns(device)
		const options = modelNumbers.map(modelNumber => {
			return <option key={modelNumber} value={modelNumber}>{modelNumber}</option>
		})
		return (
			<Form.Select id="model-number-pattern" onChange={modelNumberPatternChanged} defaultValue={modelNumberPatternSignal.value}>
				<option value="">Select Model Number Pattern</option>
				{options}
			</Form.Select>
		)
	}

	const handleRunImmediatelyChanged = function () {
		runImmediatelySignal.value = !runImmediatelySignal.value
		// updateApplyButtonStatus()
	}

	return (
		
		<Modal
			show={showSignal.value}
			onHide={handleCancel}
			backdrop="static"
			size={'xxl'}
			style={{"--bs-modal-width":"calc(100vw - 70px)"}}
		>

			<Modal.Header closeButton>
				{
					// ternary operator to only show the correct title
					taskType == TaskType.UpdateDeviceForms ?
					<Modal.Title>Push Forms</Modal.Title> :
					<Modal.Title>Update Firmware</Modal.Title>
				}
			</Modal.Header>
			<Modal.Body>
				
				{
					taskType == TaskType.UpdateDeviceForms ?
					<p>This action will update the form on {selectedDevices.length} device(s)</p> :
					<p>This action will update the firmware on {selectedDevices.length} device(s)</p>
				}

				<Form name="pushForms" id="pushForms" role="form">
				{
					// firmware specific form inputs
					taskType == TaskType.UpdateDeviceFirmware && devicePciVersionSignal.value == 0 ?
					<div>
						<Alert variant="warning">
							<Alert.Heading>The selected device(s) have a PCI Version of 0</Alert.Heading>
							<p>We are unable to determine the device based on the PCI Version. Please verify the model number in the dropdown.</p>
							<hr />
							<p className="mb-0">
							<span className="strong">Warning</span>: Specifying the wrong model would cause your device to become inoperable and require a replacement. Please make sure you have specified the correct model number. Typically, the model number can be found at the bottom of the device
							</p>
						</Alert>
						<Form.Group className="mb-3">
							<Form.Label htmlFor="device">Device</Form.Label>
							<Form.Select id="device" onChange={deviceChanged} defaultValue=''>
								<option value="">Select a device</option>
								<option value="ipp320">IPP320</option>
								<option value="ipp350">IPP350</option>
								<option value="isc250">iSC250</option>
								<option value="iup250">iUP250</option>
								<option value="iwl220">IWL220</option>
								<option value="iwl250">IWL250</option>
								<option value="iwl252">IWL252</option>
								<option value="iwl228">IWL228</option>
								<option value="iwl258">IWL258</option>
								<option value="isc480">iSC480</option>
								<option value="icm122">ICM122</option>
								<option value="imp322">IMP322</option>
								<option value="imp352">IMP352</option>
								<option value="imp657">IMP657</option>
								<option value="imp627">IMP627</option>
							</Form.Select>
						</Form.Group>
						<Form.Group className="mb-3" hidden={deviceSignal.value == ""}>
							<Form.Label htmlFor="model-number-pattern">Model Number Pattern</Form.Label>
							<ModelNumberPatternSelect device={deviceSignal.value}></ModelNumberPatternSelect>
						</Form.Group>
					</div> : ""
				}

					<Form.Group className="mb-3 table-responsive" style={{height:"calc(100vh - 538px)"}}>
						<Form.Label htmlFor="component">Select Form</Form.Label>
						<Table striped hover id='deviceFormsTable'>
							<thead>
								<tr>
									<TableColumnHeader title={'Select'} sortable={false}></TableColumnHeader>
									<TableColumnHeader title={'Name'} sortable={true} datafield={'name'}></TableColumnHeader>
									<TableColumnHeader title={'Device Type'} sortable={true} datafield={'deviceType'}></TableColumnHeader>
									<TableColumnHeader title={'Upload Date'} sortable={true} datafield={'lastUpdatedAt'}></TableColumnHeader>
								</tr>
							</thead>
							<TableRows items={deviceFormsSignal.value} />
						</Table>
					</Form.Group>
					<Form.Group className="mb-3" id="daterangepickerDiv" hidden={runImmediatelySignal.value}>
						<div disabled={runImmediatelySignal.value}>
							<Form.Label className="inline margin-right" htmlFor="daterangepicker">Date</Form.Label>
							<DatePicker
								showIcon
								id="daterangepicker"
								className="d-block form-control"
								showTimeSelect
								onChange={
									(value) => {
										dateSignal.value = value
									}
								}
								readonly={runImmediatelySignal.value}
								required={!runImmediatelySignal.value}
								selected={dateSignal.value}
								timeFormat="HH:mm"
								timeIntervals={30} // Interval in minutes for the time picker
								timeCaption="Time" // Caption for the time picker
								dateFormat="yyyy-MM-dd h:mm aa" // Include both date and time
								shouldCloseOnSelect={true}
							/>
							<p className="py-3">UTC: {dateSignal.value.toISOString()}</p>
						</div>
					</Form.Group>
					<Form.Group className="mb-3" controlId="runImmediately.ControlInput">
						<Form.Check id="runImmediately" name="runImmediately" type="checkbox" checked={runImmediatelySignal.value} onChange={handleRunImmediatelyChanged} label="Run Immediately"></Form.Check>
					</Form.Group>
					<Form.Group className="mb-3" hidden={!runImmediatelySignal.value}>
						<p><strong>Warning</strong>: This will run the chosen type immediately which could result in the UTG being down. It is recommended to run a task outside of business hours.</p>
					</Form.Group>
					<hr />
					<Form.Group className="mb-3" controlId="taskLabel.ControlInput">
						<Form.Label className="d-block">Task Label</Form.Label>
						<Form.Control type="text" className="d-block" placeholder="Optional task label" onChange={(event) => bulkLabelSignal.value = event.target.value} value={bulkLabelSignal.value} />
					</Form.Group>
				</Form>
			</Modal.Body>
            <Modal.Footer>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                    disabled={!enableApplyComputed.value}
                    variant="primary" 
					onClick={pushFormToDevice}
				>Apply</Button>
            </Modal.Footer>
		</Modal>
	)
}

export default PushFormsModal;
